import { useState, useEffect } from 'react';
import Button from '../shared/Button';
import { X } from 'lucide-react';
import { InfoTooltip } from '../shared/InfoTooltip';
import { DocumentService } from '../../services/DocumentService';
import { Toast } from '../shared/Toast';
import TextFieldWithCounter from '../shared/TextFieldWithCounter';

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal: 'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px]',
	heading: 'text-25 font-bold text-dark-blue xl:mb-3 md:mb-1 leading-100',
	subHeading: 'text-gray text-sm xl:mb-6 md:mb-2',
	label: 'text-base leading-100 font-medium text-dark-blue',
	note: 'bg-lightPurple p-4 rounded-xl text-sm leading-21 text-dark-blue font-medium italic xl:mb-8 mb-3 flex flex-col',
	span: 'font-semibold text-base leading-100 text-purple xl:mb-2.5 mb-1'
};

export default function EditDocumentModal({
	isOpen,
	onClose,
	documentId,
	currentTitle,
	onDocumentUpdated // Callback to refresh document data after update
}) {
	const [documentTitle, setDocumentTitle] = useState(currentTitle || '');
	const [isLoading, setIsLoading] = useState(false);

	// Commented out for now
	// const [formattingStyle, setFormattingStyle] = useState('APA 7th Edition');
	// const [university, setUniversity] = useState('University of Texas at Austin');
	// const formattingStyles = ['APA 7th Edition', 'MLA 9th Edition', 'Chicago Style'];
	// const universities = ['University of Texas at Austin', 'Harvard University', 'Yale University'];

	// Reset state when modal opens or currentTitle changes
	useEffect(() => {
		if (isOpen && currentTitle) {
			setDocumentTitle(currentTitle);
		}
	}, [isOpen, currentTitle]);

	if (!isOpen) return null;

	const handleUpdateDocument = async () => {
		// Validate
		if (!documentTitle || documentTitle.trim() === '') {
			Toast.error('Document title cannot be empty');
			return;
		}

		if (!documentId) {
			Toast.error('Document ID is missing');
			return;
		}

		// Check if title actually changed
		if (documentTitle.trim() === currentTitle) {
			Toast.info('No changes to save');
			onClose();
			return;
		}

		try {
			setIsLoading(true);

			// Call the rename API
			await DocumentService.renameDocument(documentId, documentTitle.trim());

			Toast.success('Document renamed successfully');

			// Call the callback to refresh document data
			if (onDocumentUpdated) {
				onDocumentUpdated();
			}

			onClose();
		} catch (error) {
			const message = error?.response?.data?.message || 'Failed to rename document';
			Toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	// COMMENTED OUT: Reusable styles for custom dropdown arrow (kept for future use)
	// const customSelectClasses =
	// 	'w-full rounded-[10px] border-1p3 border-[#E8E8EA] px-4 xl:py-3 py-2 text-base appearance-none bg-no-repeat focus:outline-none focus:ring-0 focus:border-[#E8E8EA]';
	// const customSelectStyle = {
	// 	backgroundImage:
	// 		"url(\"data:image/svg+xml;utf8,<svg width='23' height='23' viewBox='0 0 23 23' fill='none' xmlns='http://www.w3.org/2000/svg'><g opacity='0.5'><path d='M16.2209 8.55273L11.7879 15.1527L7.4209 8.55273H16.2209Z' fill='%23180A63'/></g></svg>\")",
	// 	backgroundSize: '23px 23px',
	// 	backgroundPosition: 'right 5px center',
	// 	paddingRight: '20px'
	// };

	return (
		<div className={styles.section}>
			{/* Backdrop */}
			<div className={styles.backDrop} onClick={onClose} />

			{/* Modal */}
			<div className={styles.modal}>
				{/* <svg
					onClick={onClose}
					className="absolute top-8 right-2 md:right-6 text-gray-400 transition cursor-pointer hover:text-dark-blue"
					width="25"
					height="26"
					viewBox="0 0 25 26"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g opacity="0.4">
						<path
							d="M8.75065 18.2085L12.5007 14.4585L16.2507 18.2085L17.709 16.7502L13.959 13.0002L17.709 9.25016L16.2507 7.79183L12.5007 11.5418L8.75065 7.79183L7.29232 9.25016L11.0423 13.0002L7.29232 16.7502L8.75065 18.2085ZM12.5007 23.4168C11.0597 23.4168 9.70551 23.1432 8.43815 22.596C7.17079 22.0488 6.06836 21.3068 5.13086 20.37C4.19336 19.4331 3.45135 18.3307 2.90482 17.0627C2.35829 15.7946 2.08468 14.4404 2.08399 13.0002C2.08329 11.5599 2.3569 10.2057 2.90482 8.93766C3.45274 7.66961 4.19475 6.56718 5.13086 5.63037C6.06697 4.69357 7.1694 3.95155 8.43815 3.40433C9.7069 2.85711 11.0611 2.5835 12.5007 2.5835C13.9402 2.5835 15.2944 2.85711 16.5632 3.40433C17.8319 3.95155 18.9343 4.69357 19.8704 5.63037C20.8066 6.56718 21.5489 7.66961 22.0975 8.93766C22.6461 10.2057 22.9194 11.5599 22.9173 13.0002C22.9152 14.4404 22.6416 15.7946 22.0965 17.0627C21.5513 18.3307 20.8093 19.4331 19.8704 20.37C18.9316 21.3068 17.8291 22.0491 16.5632 22.597C15.2972 23.145 13.943 23.4182 12.5007 23.4168Z"
							fill="#180A63"
						/>
					</g>
				</svg> */}
				<button
					onClick={onClose}
					className="absolute top-8 right-2 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{/* Heading */}
				<h2 className={styles.heading}>Document Settings</h2>
				<p className={styles.subHeading}>Update document settings below</p>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* Input fields */}
				<div className="grid grid-cols-1 gap-6 xl:mb-4 mb-2">
					{/* Document Title */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="document-title" className={styles.label}>
								Change document name
							</label>
							<InfoTooltip text="Enter a new name for your document" />
						</div>
						<TextFieldWithCounter
							className="focus:outline-none focus:border-purple"
							id="document-title"
							placeholder="Enter Document Name"
							value={documentTitle}
							onChange={(e) => setDocumentTitle(e.target.value)}
							minLength={3}
							maxLength={75}
						/>
					</div>

					{/* COMMENTED OUT: Formatting Style */}
					{/* <div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="formatting-style" className={styles.label}>
								Change document format
							</label>
							<InfoTooltip />
						</div>
						<select
							id="formatting-style"
							value={formattingStyle}
							onChange={(e) => setFormattingStyle(e.target.value)}
							className={customSelectClasses}
							style={customSelectStyle}
						>
							<option value="" disabled hidden>
								Select style
							</option>
							{formattingStyles.map((style) => (
								<option key={style} value={style}>
									{style}
								</option>
							))}
						</select>
					</div> */}

					{/* COMMENTED OUT: University */}
					{/* <div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="university" className={styles.label}>
								Change university name
							</label>
							<InfoTooltip />
						</div>
						<select
							id="university"
							value={university}
							onChange={(e) => setUniversity(e.target.value)}
							className={customSelectClasses}
							style={customSelectStyle}
						>
							<option value="" disabled hidden>
								Select university
							</option>
							{universities.map((uni) => (
								<option key={uni} value={uni}>
									{uni}
								</option>
							))}
						</select>
					</div> */}
				</div>

				{/* COMMENTED OUT: Important note */}
				{/* <div className={styles.note}>
					<span className={styles.span}>Important note:</span>This document will only follow{' '}
					{formattingStyle} formatting.
				</div> */}

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* Buttons */}
				<div className="flex xl:gap-3 gap-2">
					<Button onClick={handleUpdateDocument} disabled={isLoading}>
						{isLoading ? 'Updating...' : 'Update Document'}
					</Button>
				</div>
			</div>
		</div>
	);
}
